
/**
 * Determines the closest point to target point along provided line segment
 *
 * @param {google.maps.LatLng} p - Target location
 * @param {google.maps.LatLng} a - First line segment endpoint
 * @param {google.maps.LatLng} b - Second line segment endpoint
 * @return {google.maps.LatLng} LatLng corresponding to closest point
 */

const closestPointOnLine = (p, a, b) => {
  const dlat = b.lat() - a.lat()
  const dlng = b.lng() - a.lng()
  if (dlat === 0 && dlng === 0) return a

  const ua = (dlat * (p.lat() - a.lat()) + dlng * (p.lng() - a.lng())) / (dlat * dlat + dlng * dlng || 0)
  if (ua <= 0) return a
  else if (ua >= 1) return b
  else return new google.maps.LatLng(a.lat() + ua * dlat, a.lng() + ua * dlng)
}

/**
 * Computes the Cartesian distance given the x,y coordinates of two points
 *
 * @param {Number} a - First coordinate of first point
 * @param {Number} b - Second coordinate of first point
 * @param {Number} c - First coordinate of second point
 * @param {Number} d - Second coordinate of second point
 * @return {Number} Cartesian distance
 */

export function distanceSquared(a, b, c, d) {
  return Math.pow(a - c, 2) + Math.pow(b - d, 2)
}

/**
 * Snaps a pixel coordinate to the closest wall object if under the SNAP_DISTANCE threshold
 *
 * @param {Point} pixel - The pixel coordinates of the mouse to snap
 * @param {google.maps.MapCanvasProjection} projection - The coordinate to pixel projection currently used by the map
 * @param {Array} walls - Array of wall objects
 * @return {google.maps.LatLng} Snapped location
 */

export default function snapToWall(pixel, projection, walls) {
  const latlng = projection.fromContainerPixelToLatLng(pixel)
  let snappedPoint
  let minDistance = Infinity
  let closestSegment = []

  walls.forEach(wall => {
    const path = wall.overlay && wall.overlay.getPath().getArray() || []
    for (let i = 0; i < path.length - 1; i++) {
      const point = closestPointOnLine(latlng, path[i], path[i + 1])
      const dist = distanceSquared(point.lat(), point.lng(), latlng.lat(), latlng.lng())

      if (dist < minDistance) {
        minDistance = dist
        snappedPoint = point
        closestSegment[0] = path[i]
        closestSegment[1] = path[i + 1]
      }
    }
  })

  if (snappedPoint) {
    const snappedPixel = projection.fromLatLngToContainerPixel(snappedPoint)
    if (Math.sqrt(distanceSquared(snappedPixel.x, snappedPixel.y, pixel.x, pixel.y)) < SNAP_DISTANCE) {
      const startPixel = projection.fromLatLngToContainerPixel(closestSegment[0])
      const endPixel = projection.fromLatLngToContainerPixel(closestSegment[1])
      const startDist = distanceSquared(startPixel.x, startPixel.y, pixel.x, pixel.y)
      const endDist = distanceSquared(endPixel.x, endPixel.y, pixel.x, pixel.y)

      if (Math.sqrt(Math.min(startDist, endDist)) < SNAP_DISTANCE) return startDist < endDist ? closestSegment[0] : closestSegment[1]
      return snappedPoint
    }
  }

  return latlng
}

/** Thresholds for snapping */

const SNAP_DISTANCE = 10
export const DOUBLE_CLICK_DISTANCE = 10



// WEBPACK FOOTER //
// ./src/helpers/walls/snapToWall.js